import type { AttributeTableMode, ContextualToolbarPosition, WorkspaceLayout } from '../core/types';

export const WORKSPACE_LAYOUT_STORAGE_KEY = 'casur-geostudio.workspace-layout.v1';
export const LEFT_PANEL_MIN = 200;
export const LEFT_PANEL_MAX = 286;
export const RIGHT_PANEL_MIN = 240;
export const RIGHT_PANEL_MAX = 520;
export const ATTRIBUTE_TABLE_MIN = 180;
export const ATTRIBUTE_TABLE_MAX = 520;
export const MIN_MAP_WIDTH = 360;
export const COLLAPSED_PANEL_WIDTH = 44;
/** The map keeps at least this height under an expanded table (the table yields first). */
export const MIN_MAP_HEIGHT = 220;
/** Height of the table header strip: all a compact table shows. */
export const TABLE_HEADER_HEIGHT = 37;
/** Smallest expanded table worth showing (header + three rows); below it the table is shown compact. */
const MIN_EXPANDED_TABLE = TABLE_HEADER_HEIGHT + 3 * 26 + 30;
/** App bar + GIS toolbar + status bar (desktop 44 + 38 + 24; mobile 44 + 40 + 22). */
export const SHELL_CHROME_HEIGHT = 106;

export const defaultContextualToolbarPosition: ContextualToolbarPosition = { position: 'default', x: 0, y: 56 };
export const defaultWorkspaceLayout: WorkspaceLayout = {
  leftPanelWidth: 286,
  leftPanelCollapsed: false,
  rightPanelWidth: 292,
  rightPanelCollapsed: false,
  attributeTableOpen: false,
  attributeTableMode: 'expanded',
  attributeTableHeight: 300,
  contextualToolbarPosition: defaultContextualToolbarPosition,
};

const clamp = (value: number, minimum: number, maximum: number): number => Math.max(minimum, Math.min(value, maximum));
const finite = (value: unknown, fallback: number): number => typeof value === 'number' && Number.isFinite(value) ? value : fallback;

export function normalizeContextualToolbarPosition(value: unknown): ContextualToolbarPosition {
  if (!value || typeof value !== 'object') return { ...defaultContextualToolbarPosition };
  const candidate = value as Partial<ContextualToolbarPosition>;
  return {
    position: candidate.position === 'free' ? 'free' : 'default',
    x: Math.max(0, finite(candidate.x, 0)),
    y: Math.max(0, finite(candidate.y, defaultContextualToolbarPosition.y)),
  };
}

export function normalizeWorkspaceLayout(value: unknown, fallback: WorkspaceLayout = defaultWorkspaceLayout): WorkspaceLayout {
  if (!value || typeof value !== 'object') return { ...fallback, contextualToolbarPosition: { ...fallback.contextualToolbarPosition } };
  const candidate = value as Partial<WorkspaceLayout>;
  return {
    leftPanelWidth: clamp(finite(candidate.leftPanelWidth, fallback.leftPanelWidth), LEFT_PANEL_MIN, LEFT_PANEL_MAX),
    leftPanelCollapsed: typeof candidate.leftPanelCollapsed === 'boolean' ? candidate.leftPanelCollapsed : fallback.leftPanelCollapsed,
    rightPanelWidth: clamp(finite(candidate.rightPanelWidth, fallback.rightPanelWidth), RIGHT_PANEL_MIN, RIGHT_PANEL_MAX),
    rightPanelCollapsed: typeof candidate.rightPanelCollapsed === 'boolean' ? candidate.rightPanelCollapsed : fallback.rightPanelCollapsed,
    attributeTableOpen: typeof candidate.attributeTableOpen === 'boolean' ? candidate.attributeTableOpen : fallback.attributeTableOpen,
    // Layouts saved before V1.7 have no mode: an open table there was always the expanded one.
    attributeTableMode: candidate.attributeTableMode === 'compact' || candidate.attributeTableMode === 'expanded' ? candidate.attributeTableMode : fallback.attributeTableMode,
    attributeTableHeight: clamp(finite(candidate.attributeTableHeight, fallback.attributeTableHeight), ATTRIBUTE_TABLE_MIN, ATTRIBUTE_TABLE_MAX),
    contextualToolbarPosition: normalizeContextualToolbarPosition(candidate.contextualToolbarPosition ?? fallback.contextualToolbarPosition),
  };
}

export function loadWorkspaceLayoutPreference(storage?: Pick<Storage, 'getItem'>): WorkspaceLayout {
  try {
    const raw = (storage ?? window.localStorage).getItem(WORKSPACE_LAYOUT_STORAGE_KEY);
    return raw ? normalizeWorkspaceLayout(JSON.parse(raw)) : normalizeWorkspaceLayout(defaultWorkspaceLayout);
  } catch {
    return normalizeWorkspaceLayout(defaultWorkspaceLayout);
  }
}

export function saveWorkspaceLayoutPreference(layout: WorkspaceLayout, storage?: Pick<Storage, 'setItem'>): void {
  try { (storage ?? window.localStorage).setItem(WORKSPACE_LAYOUT_STORAGE_KEY, JSON.stringify(normalizeWorkspaceLayout(layout))); } catch { /* El almacenamiento privado puede estar deshabilitado. */ }
}

export function clampFloatingPosition(
  position: ContextualToolbarPosition,
  bounds: Readonly<{ width: number; height: number }>,
  size: Readonly<{ width: number; height: number }>,
): ContextualToolbarPosition {
  if (position.position === 'default') return { ...position };
  return {
    ...position,
    x: clamp(position.x, 0, Math.max(0, bounds.width - size.width)),
    y: clamp(position.y, 0, Math.max(0, bounds.height - size.height)),
  };
}

export interface EffectiveSidePanels { readonly leftWidth: number; readonly rightWidth: number; readonly autoCollapseLeft: boolean; readonly autoCollapseRight: boolean }

export function effectiveSidePanels(layout: WorkspaceLayout, workspaceWidth: number): EffectiveSidePanels {
  let leftWidth = layout.leftPanelCollapsed ? COLLAPSED_PANEL_WIDTH : layout.leftPanelWidth;
  let rightWidth = layout.rightPanelCollapsed ? COLLAPSED_PANEL_WIDTH : layout.rightPanelWidth;
  let autoCollapseLeft = false; let autoCollapseRight = false;
  const available = Math.max(0, workspaceWidth - MIN_MAP_WIDTH - 2);
  if (leftWidth + rightWidth > available && !layout.leftPanelCollapsed && !layout.rightPanelCollapsed) {
    const reducibleLeft = Math.max(0, leftWidth - LEFT_PANEL_MIN); const reducibleRight = Math.max(0, rightWidth - RIGHT_PANEL_MIN);
    const reduction = Math.min(leftWidth + rightWidth - available, reducibleLeft + reducibleRight);
    if (reduction > 0) {
      const total = reducibleLeft + reducibleRight;
      const leftReduction = total ? reduction * reducibleLeft / total : 0;
      leftWidth -= leftReduction; rightWidth -= reduction - leftReduction;
    }
  }
  if (leftWidth + rightWidth > available && !layout.rightPanelCollapsed) { rightWidth = COLLAPSED_PANEL_WIDTH; autoCollapseRight = true; }
  if (leftWidth + rightWidth > available && !layout.leftPanelCollapsed) { leftWidth = COLLAPSED_PANEL_WIDTH; autoCollapseLeft = true; }
  return { leftWidth: Math.round(leftWidth), rightWidth: Math.round(rightWidth), autoCollapseLeft, autoCollapseRight };
}

export interface EffectiveTable { readonly state: 'closed' | AttributeTableMode; readonly height: number; readonly autoCompact: boolean }

/**
 * What the table dock really occupies in a window of `viewportHeight` px: the stored height is a preference, never a
 * reason to squeeze the map below MIN_MAP_HEIGHT. A window too short for a useful expanded table shows it compact.
 */
export function effectiveTable(layout: WorkspaceLayout, viewportHeight: number): EffectiveTable {
  if (!layout.attributeTableOpen) return { state: 'closed', height: 0, autoCompact: false };
  if (layout.attributeTableMode === 'compact') return { state: 'compact', height: TABLE_HEADER_HEIGHT, autoCompact: false };
  const maximum = Math.min(ATTRIBUTE_TABLE_MAX, viewportHeight - SHELL_CHROME_HEIGHT - MIN_MAP_HEIGHT);
  if (maximum < MIN_EXPANDED_TABLE) return { state: 'compact', height: TABLE_HEADER_HEIGHT, autoCompact: true };
  return { state: 'expanded', height: Math.round(clamp(layout.attributeTableHeight, MIN_EXPANDED_TABLE, maximum)), autoCompact: false };
}
